import { cn } from '@/lib/utils'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { isCheckoutTemplate } from '@/utils/checkoutTemplates'

const WIDE_QUERY = '(min-width: 1280px)'

function subscribeWide(onStoreChange: () => void) {
  const media = window.matchMedia(WIDE_QUERY)
  media.addEventListener('change', onStoreChange)
  return () => media.removeEventListener('change', onStoreChange)
}

function readWide() {
  return window.matchMedia(WIDE_QUERY).matches
}

export function useWideCheckoutPreview() {
  return useSyncExternalStore(subscribeWide, readWide, () => false)
}

/** Override local (ex.: localhost:3000). Em produção vem `checkout_base_url` do GET. */
function envCheckoutPublicBase() {
  const raw = import.meta.env.VITE_CHECKOUT_PUBLIC_URL
  if (typeof raw !== 'string') return ''
  return raw.trim().replace(/\/+$/, '')
}

function resolveCheckoutPublicBase(checkoutBaseUrl?: string) {
  const fromApi = checkoutBaseUrl?.trim().replace(/\/+$/, '') ?? ''
  return envCheckoutPublicBase() || fromApi
}

type CheckoutLivePreviewProps = {
  checkoutId: string
  checkoutBaseUrl?: string
  template?: string
  reloadToken: number
  className?: string
}

export function CheckoutLivePreview({
  checkoutId,
  checkoutBaseUrl,
  template,
  reloadToken,
  className,
}: CheckoutLivePreviewProps) {
  const { t } = useTranslation(['checkouts'])
  const [loaded, setLoaded] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const base = resolveCheckoutPublicBase(checkoutBaseUrl)
  const safeTemplate = isCheckoutTemplate(template) ? template : 'default'
  const frameKey = `${safeTemplate}-${reloadToken}-${attempt}`

  useEffect(() => {
    setLoaded(false)
  }, [frameKey, base])

  if (!base || !checkoutId) {
    return (
      <div className="rounded-2xl border border-colorBorder bg-[#18181B] p-4">
        <h2 className="text-sm font-semibold text-white">
          {t('checkouts:preview_title')}
        </h2>
        <p className="mt-2 text-sm leading-5 text-muted">
          {t('checkouts:preview_missing_url')}
        </p>
        <button
          type="button"
          className="mt-4 inline-flex min-h-11 items-center rounded-full bg-[#27272A] px-4 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#18181B]"
          onClick={() => window.location.reload()}
        >
          {t('checkouts:preview_retry')}
        </button>
      </div>
    )
  }

  const src = `${base}/${encodeURIComponent(checkoutId)}?template=${encodeURIComponent(safeTemplate)}&preview=1`

  return (
    <div
      className={cn(
        'flex h-full min-h-0 max-h-full flex-col gap-3 overflow-hidden rounded-2xl border border-colorBorder bg-[#18181B] p-3',
        className,
      )}
    >
      <div className="min-w-0 pr-8">
        <h2 className="text-sm font-semibold text-white">
          {t('checkouts:preview_title')}
        </h2>
        <p className="mt-1 text-sm leading-5 text-muted">
          {t('checkouts:preview_caption')}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 justify-center overflow-hidden rounded-xl bg-black">
        <div className="relative h-full w-full">
          {loaded ? null : (
            <div
              className="pointer-events-none absolute inset-0 bg-zinc-800 motion-safe:animate-pulse"
              aria-hidden
            />
          )}
          <iframe
            key={frameKey}
            src={src}
            title={t('checkouts:preview_frame_title')}
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
            className="h-full min-h-0 w-full border-0 bg-white"
          />
        </div>
      </div>
    </div>
  )
}
